import { useEffect } from "react";

import {
  DEFAULT_NEEDS_REVIEW_STATUS,
  useNeedsReviewQueue,
} from "@/features/cosignature/api/queries/useNeedsReviewQueue";
import { Pagination } from "@/features/pagination/Pagination";
import { useCursorPagination } from "@/features/pagination/hooks/useCursorPagination";
import { usePrefetchCosignatureQueue } from "@/features/cosignature/api/queries/usePrefetchCosignatureQueue";

import { type CosignatureFilterProps, CosignatureFilters } from "../(filters)/CosignatureFilters";
import type { CosignatureTab } from "../types";
import { CosignatureTable } from "../(table)/CosignatureTable";
import { QUEUE_ROW_COUNT } from "../constants";

type CosignaturePanelProps = {
  filters: CosignatureFilterProps;
  onClearFilters?: VoidFunction;
  tab: CosignatureTab;
};

export function NeedsReviewPanel({ filters, onClearFilters, tab }: CosignaturePanelProps) {
  const {
    currentPage,
    currentStartKey,
    maxNavigablePage,
    onPageChange,
    registerNextCursor,
    resetPage,
  } = useCursorPagination();

  const baseSearchParams = {
    cosignature_status: filters.status ?? DEFAULT_NEEDS_REVIEW_STATUS,
    page_size: QUEUE_ROW_COUNT,
    supervisor_id: filters.supervisor_id ?? undefined,
  };

  const { data, isFetching, isLoading, isPreviousData } = useNeedsReviewQueue({
    ...baseSearchParams,
    start_key: currentStartKey,
  });

  // keepPreviousData masks filter changes as `isLoading: false` — treat showing
  // stale (previous-filter) data while refetching as loading too
  const isQueueLoading = isLoading || (isFetching && isPreviousData);

  usePrefetchCosignatureQueue({ baseSearchParams, currentPage, data, registerNextCursor });

  useEffect(() => {
    registerNextCursor(currentPage, data?.next_start_key);
  }, [data?.next_start_key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    resetPage();
  }, [filters.status, filters.supervisor_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tw:relative tw:flex tw:flex-col tw:gap-4">
      <CosignatureFilters tab={tab} {...filters} />
      <CosignatureTable data={data} isLoading={isQueueLoading} onClearFilters={onClearFilters} />

      <Pagination
        className="tw:absolute tw:right-0 tw:-bottom-14 tw:left-0 tw:justify-center"
        currentPage={currentPage}
        isLoading={isQueueLoading}
        maxNavigablePage={maxNavigablePage}
        onPageChange={onPageChange}
        totalPages={data?.total_page_count ?? 1}
      />
    </div>
  );
}
